import Skeleton from "@components/skeleton/Skeleton";
import { composeRefHandlers } from "@utils/general";
import autosize from "autosize";
import React from "react";

import { KeyName } from "../../../keyName";
import TestIds from "../../../testIds";
import { WithErrorAndTooltip, withErrorAndTooltip } from "../formError/WithErrorAndTooltip";
import { IInputOnChangeEvent, ISharedInputProps } from "../input";
import { ReadOnlyText } from "../utils";
import { StyledInput, StyledTextArea, Wrapper } from "./TextArea.styles";

interface TextAreaProps extends ISharedInputProps, WithErrorAndTooltip {
    value: string;
    maxRows?: number;
    minRows?: number;
    isSkeleton?: boolean;
    passRef?: React.Ref<HTMLTextAreaElement>;
    outerRef?: React.RefObject<HTMLDivElement>;
    onChange: (e: IInputOnChangeEvent<string>) => void;
    onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
    onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
    onKeyDown?: (event: React.KeyboardEvent) => void;
    className?: string;
}

interface ITextAreaSkeletonProps extends Partial<TextAreaProps> {
    isSkeleton: true;
}

class TextArea extends React.PureComponent<TextAreaProps | ITextAreaSkeletonProps> {
    textAreaRef = React.createRef<HTMLTextAreaElement>();
    _autoSizedValue: string; // value autosize has been calculated according to

    componentDidMount() {
        if (!this.props.isReadOnly) {
            autosize(this.textAreaRef.current);
            this._autoSizedValue = this.getValue();
        }
    }

    componentDidUpdate(prevProps: Readonly<TextAreaProps>) {
        if (!this.props.isReadOnly && this.getValue() !== this._autoSizedValue) {
            autosize.update(this.textAreaRef.current);
            this._autoSizedValue = this.getValue();
        }
    }

    getValue = () => {
        // return empty string if null given, otherwise textarea has problem differentiate if its controlled or uncontrolled
        return this.props.value ?? "";
    };

    handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        this._autoSizedValue = e.target.value;
        this.props?.onChange({
            value: e.target.value,
            origEvent: e
        });
    };

    handleKeyDown = (e: React.KeyboardEvent) => {
        // enter is handled by textarea itself, we do not permit any additional
        // actions (like closing dialog)
        if (e.key === KeyName.Enter) {
            e.stopPropagation();
        }

        this.props.onKeyDown?.(e);
    };


    renderTextArea(): React.ReactNode {
        if (this.props.isSkeleton) {
            return <Skeleton width={"120px"} height={"16px"}/>;
        }

        const isSingleLine = this.props.maxRows === 1;
        const Component: any = isSingleLine ? StyledInput : StyledTextArea;

        return (
            <Wrapper ref={this.props.outerRef} className={this.props.className} data-testid={TestIds.Input}>
                {this.props.errorAndTooltip}
                <Component
                    disabled={this.props.isDisabled}
                    maxRows={this.props.maxRows}
                    ref={composeRefHandlers(this.textAreaRef, this.props.passRef)}
                    placeholder={this.props.placeholder}
                    value={this.getValue()}
                    width={this.props.width}
                    name={this.props.name}
                    rows={this.props.minRows}
                    onFocus={this.props.onFocus}
                    onBlur={this.props.onBlur}
                    autoComplete={"off"}
                    onKeyDown={this.handleKeyDown}
                    onChange={this.handleChange}/>
            </Wrapper>
        );
    }

    render(): React.ReactNode {
        return this.props.isReadOnly && !this.props.auditTrailData?.type ? (<ReadOnlyText {...this.props} />) : this.renderTextArea();
    }
}

export { TextArea as PureTextArea };
export default withErrorAndTooltip(TextArea, { preventValueTooltip: true });